import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import {
  Calendar,
  Events,
  Home,
  List,
  News,
  NoRunners,
  SidebarMenu as SidebarMenuTornado,
  SidebarMenuItem,
  Envelope,
  FactCheck,
  Form,
} from '@pmu/tornado';

import { getUser } from '@api/services';
import { User } from '@api/types';
import { ROUTES } from '@config/routes';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { extractUserTagPayload } from '@services/helpers';
import { sendTag } from '@services/SendTag';
import { useAppSelector } from '@store/hooks';
import { replaceStartDate } from '@utils/date';

import { FirstLoginModal } from './components/FirstLoginModal';
import { SidebarMenuItemFeedback } from './components/SidebarMenuItemFeedback';

const SidebarMenu = () => {
  const { atMost } = useMediaQuery();
  const translations = useIntl();
  const navigate = useNavigate();

  const [isFirstLoginModalOpen, setIsFirstLoginModalOpen] = useState(false);

  const sidebarMenuItemLabels = {
    home: translations.formatMessage({ id: 'app.categories.home' }),
    meetingsCalendar: translations.formatMessage({ id: 'app.categories.meetingsCalendar' }),
    programme: translations.formatMessage({ id: 'app.categories.programme' }),
    nonRunners: translations.formatMessage({ id: 'app.categories.nonRunners' }),
    racingEvents: translations.formatMessage({ id: 'app.categories.racingEvents' }),
    'quinte+': translations.formatMessage({ id: 'app.categories.quinte' }),
    pronostics: translations.formatMessage({ id: 'app.categories.pronostics' }),
    publications: translations.formatMessage({ id: 'app.categories.publications' }),
    proposal: translations.formatMessage({ id: 'app.categories.proposal' }),
    firstLogin: translations.formatMessage({ id: 'app.categories.firstLogin' }),
  };

  const user = useAppSelector<User>(getUser as () => User);

  const payloadForGTM = extractUserTagPayload(user);

  const sendDashboardTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.DASHBOARD));
    sendTag('infocentre.clic.sidebar.dashboard', payloadForGTM);
  };

  const sendProgramTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.RACECARDS));
    sendTag('infocentre.clic.sidebar.programme', payloadForGTM);
  };

  const sendQuinteTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.QUINTE));
    sendTag('infocentre.clic.sidebar.quinte_jour', payloadForGTM);
  };

  const sendNonRunnersTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.NON_RUNNERS));
    sendTag('infocentre.clic.sidebar.non_partants', payloadForGTM);
  };

  const navigateToRacingEvents = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.RACING_EVENTS));
  };

  const sendPublicationsTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(replaceStartDate(ROUTES.PUBLICATIONS));
    sendTag('infocentre.clic.sidebar.publications', payloadForGTM);
  };

  const sendProposalTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(ROUTES.PROPOSAL);
    sendTag('infocentre.clic.sidebar.proposal', payloadForGTM);
  };

  const sendFirstLoginTag = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsFirstLoginModalOpen(true);
    sendTag('infocentre.clic.sidebar.first_login', payloadForGTM);
  };

  const sendMeetingsCalendarTag = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(ROUTES.MEETINGS_CALENDAR);
    sendTag('infocentre.clic.sidebar.calendrier_reunion', payloadForGTM);
  };

  const sendOnToggleTag = (isOpened: boolean) => {
    const eventName = isOpened
      ? 'infocentre.clic.sidebar.ouvrir'
      : 'infocentre.clic.sidebar.fermer';
    sendTag(eventName, payloadForGTM);
  };

  const isOtpUser = user.internal ?? false;

  const location = useLocation();

  const getPagePath = (route: string) => {
    const isCurrentPage = location.pathname.startsWith(route.split(':')[0]);

    return isCurrentPage ? location.pathname : replaceStartDate(route);
  };

  return (
    <>
      <SidebarMenuTornado mini={atMost} onToggle={sendOnToggleTag} data-testid="app/menu">
        <SidebarMenuItem
          to={replaceStartDate(ROUTES.DASHBOARD)}
          Icon={Home}
          label={sidebarMenuItemLabels.home}
          onClick={sendDashboardTag}
        />
        <SidebarMenuItem
          to={ROUTES.MEETINGS_CALENDAR}
          Icon={Calendar}
          label={sidebarMenuItemLabels.meetingsCalendar}
          onClick={sendMeetingsCalendarTag}
        />
        <SidebarMenuItem
          to={getPagePath(ROUTES.RACECARDS)}
          Icon={List}
          label={sidebarMenuItemLabels.programme}
          onClick={sendProgramTag}
        />
        <SidebarMenuItem
          to={getPagePath(ROUTES.QUINTE)}
          Icon={Events}
          label={sidebarMenuItemLabels['quinte+']}
          onClick={sendQuinteTag}
        />
        <SidebarMenuItem
          to={getPagePath(ROUTES.NON_RUNNERS)}
          Icon={NoRunners}
          label={sidebarMenuItemLabels.nonRunners}
          onClick={sendNonRunnersTag}
        />
        <SidebarMenuItem
          to={getPagePath(ROUTES.RACING_EVENTS)}
          Icon={News}
          label={sidebarMenuItemLabels.racingEvents}
          onClick={navigateToRacingEvents}
        />
        <SidebarMenuItem
          to={getPagePath(ROUTES.PUBLICATIONS)}
          Icon={Form}
          label={sidebarMenuItemLabels.publications}
          onClick={sendPublicationsTag}
        />
        {isOtpUser ? (
          <>
            <SidebarMenuItem
              to={ROUTES.PROPOSAL}
              Icon={FactCheck}
              label={sidebarMenuItemLabels.proposal}
              onClick={sendProposalTag}
            />
            <SidebarMenuItem
              to={ROUTES.FIRST_LOGIN}
              Icon={Envelope}
              label={sidebarMenuItemLabels.firstLogin}
              onClick={sendFirstLoginTag}
            />
          </>
        ) : null}
        <SidebarMenuItemFeedback />
      </SidebarMenuTornado>
      <FirstLoginModal
        isOpen={isFirstLoginModalOpen}
        onClose={() => setIsFirstLoginModalOpen(false)}
      />
    </>
  );
};

export { SidebarMenu };
